import React, { useState } from 'react';
import { FileUpload, IUploadedFile } from '../FileUpload';
import { AlleleFormat, LabClient, ProfilesClient } from '@app/api2/api';
import { Container, FormDropdown, Form, Message, Segment } from 'semantic-ui-react';
import { buildUrl, AppRoutes } from '../AppRoutes';
import SemanticField from '@app/components/SemanticField';
import { toAlleleFormatName } from '@common/AlleleFormatNames';
import { FormErrorList } from '../FormErrorList';
import * as yup from "yup";
import { BPForm } from '@common/BPForm';
import { PageHeader } from '@common/PageHeader';
import { insertComponentsIntoText } from '@common/Utils';
import { useLabs } from '../Hooks/UseLabs';
import { useKits } from '../Hooks/UseKits';
import { BPContainer } from '@/components/bp/bpcontainer';


interface Values {
    labId: number;
    kitId: number;
    file: IUploadedFile;
    format?: AlleleFormat;
}

interface Preview {
    isUAS: boolean;
    previewErrors: string[];
    showPreviewErrors: boolean;
    showSampleNames: boolean;
    sampleNames: Array<{ name: string, message: string }>;
    serverErrors: string[];
    showServerErrors: boolean;
}

export const ProfilesEvidenceUpload = () => {
    const [preview, setPreview] = useState<Preview>({ isUAS: false, previewErrors: [], showPreviewErrors: false, showSampleNames: false, sampleNames: [], showServerErrors: false, serverErrors: [] });
    const labClient = new LabClient();
    const profilesClient = new ProfilesClient();
    const labs = useLabs({})
    const kits = useKits({labId: labs.selectedId, deps: [labs.selectedId]});

    const previewSample = async (file: IUploadedFile, labId: number, kitId: number, format: AlleleFormat) => {

        if (!file) {
            setPreview({ isUAS: false, showPreviewErrors: false, showSampleNames: false, previewErrors: [], sampleNames: [], serverErrors: [], showServerErrors: false });
            return { successful: true };
        }
        if (!labId) {
            return { successful: false };
        }

        const result = await profilesClient.previewEvidence(labId, {
            kitId: kitId,
            evidenceFile: file.contents,
            format: format
        });

        setPreview({
            isUAS: result.isUAS,
            showPreviewErrors: (result.errors || []).length > 0,
            showSampleNames: result.successful,
            previewErrors: (result.errors || []).map((e) => e.value),
            sampleNames: result.successful ? result.newEvidence.map((e) => ({ name: e.name, message: e.warning })) : [],
            serverErrors: [],
            showServerErrors: false
        });

        return result;
    };

    const uploadSample = async (values: Values): Promise<any> => {
        const result = await profilesClient.createEvidence(values.labId, null,
            {
                kitId: values.kitId,
                evidenceFile: values.file.contents,
                format: values.format,
            });

        if (result.successful) {
            window.location.assign(buildUrl(AppRoutes.Profiles, {}));
        } else {
            setPreview({
                ...preview,
                serverErrors: result.errors.map((e) => e.value),
                showServerErrors: true,
            });
        }
    };

    return (
        <BPContainer>
            <PageHeader header={insertComponentsIntoText(window.translation.evidenceView.uploadTitle, {
                '{caseName}': ''
            })} />
            <Segment>
                <BPForm
                    initialValues={{ labId: labs.selectedId ?? null as number, kitId: kits.selectedId ?? null as number, file: null as IUploadedFile, format: null as AlleleFormat }}
                    enableReinitialize={true}
                    vaildationSchema={yup.object().shape({
                        labId: yup.mixed().required().label(window.translation.common.lab),
                        kitId: yup.mixed().required().label(window.translation.common.kit),
                        file: yup.mixed().required().test('validateSample', async (value, ctx) => {
                            let isValid = preview.previewErrors.length == 0;

                            if (!isValid) {
                                let err = ctx.createError({ message: preview.previewErrors.join(". ") })
                                return err;
                            }
                            return isValid;
                        }).nullable(true).label(window.translation.evidence.evidenceFile),

                    }) as any}
                    onSubmit={async (values, formik) => {
                        await uploadSample(values);
                    }}
                    canSubmit={(values, formik) => {
                        return formik.isValid && (!preview.showPreviewErrors);
                    }}>
                    {formik => (
                        <>
                            {labs.items.length > 1 && <SemanticField
                                label={window.translation.common.lab}
                                name={'labId'}
                                component={Form.Dropdown}
                                options={labs.items}
                                selection
                                required
                                value={labs.selectedId}
                                onChange={async (e, dataLabId) => {
                                    labs.setSelectedId(dataLabId);

                                    await previewSample(formik.values.file, dataLabId, formik.values.kitId, formik.values.format);
                                }} />}
                            {/* <SemanticField
                                label={window.translation.common.lab}
                                name={'labId'}
                                component={Input}
                                options={[]}
                                value={labs.selectedId}
                                required
                                onChange={async (e, dataLabId) => {
                                    labs.setSelectedId(dataLabId);
                                    await previewSample(formik.values.file, dataLabId, formik.values.kitId, formik.values.format);
                                }} /> */}

                            <SemanticField
                                label={window.translation.common.kit}
                                name={'kitId'}
                                required
                                component={Form.Dropdown}
                                deps={['labId']}
                                selection
                                options={kits.items}
                                value={kits.selectedId}
                                onChange={async (e, dataKitId) => {
                                    kits.setSelectedId(dataKitId);
                                    await previewSample(formik.values.file, formik.values.labId, dataKitId, formik.values.format);
                                }} />

                            <SemanticField
                                id="file"
                                name="file"
                                required
                                label={window.translation.evidence.evidenceFile}
                                fileUploaded={async (name: string, data: IUploadedFile) => {
                                    await previewSample(data, formik.values.labId, formik.values.kitId, formik.values.format);
                                    await formik.setFieldValue('file', data);
                                    await formik.setFieldTouched('file', true, true);
                                }}
                                component={FileUpload}
                                validateOnChange={true} />

                            {preview?.isUAS &&
                                <SemanticField component={FormDropdown}
                                    id="format"
                                    name="format"
                                    selection options={[]}
                                    deps={['labId']}
                                    optionsSource={async () => {
                                        const formats = (await labClient.getAlleleFormats(formik.values.labId)).map((format) => ({ key: `format${format}`, value: format, text: toAlleleFormatName(format) }));
                                        return formats;
                                    }}
                                    onChange={async (e, dataFormat) => {
                                        await previewSample(formik.values.file, formik.values.labId, formik.values.kitId, dataFormat);
                                    }}
                                    label={window.translation.sample.sampleInUAS} />
                            }
                            <FormErrorList message={window.translation.sample.fileErrors} show={preview.showServerErrors} errors={preview.serverErrors} />
                            {/* <FormErrorList message={window.translation.sample.fileCannotBeProcessed} show={preview?.showPreviewErrors} errors={preview?.previewErrors} /> */}
                            {preview?.showSampleNames && <Message info header={window.translation.sample.sampleNames} list={(preview?.sampleNames ?? []).map((m, i) => `${m.name} ${m.message ? `(${m.message})` : ""}`)} />}

                        </>

                    )}
                </BPForm>
            </Segment>
        </BPContainer>

    );
};